import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { usePrintStore } from '@renderer/stores/printStore'
import {
  processAndUploadStrip,
  processAndUploadGIF,
  processAndUploadPhotos,
  stateUploader
} from '@renderer/utils/pipeline/outputOrchestrator'
import { initiatePrint } from '@renderer/utils/pipeline/printService'
import type { ProcessingContext } from '@renderer/types/outputPipeline.types'
import { backgroundVideoProcessor } from '@renderer/utils/backgroundVideoProcessor'
import { FrameConfig } from '@renderer/types/configState'
import Button from '@renderer/components/bahela/Button'
import PrintPreview, { PrintPreviewRef, type ExportResult } from '@renderer/components/PrintPreview'
import { FaExchangeAlt, FaQrcode } from 'react-icons/fa'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { waitPrintResultMinDisplay } from '@renderer/utils/printResultMinDisplay'
import { resolveFramedVideoPlaybackAndRemote } from '@renderer/utils/galleryUrlUtils'
import { navigateAfterOutputsReady } from '@renderer/utils/navigateAfterOutputs'

// Import Bahela assets
import bgLayer from '/assets/bahela/bg-layer.webp'
import bahelaLogo from '/assets/bahela/bahela-pink.webp'
import loadingImage from '/assets/bahela/loading.webp'
import cogoToast from '@successtar/cogo-toast'

interface UserContext {
  user?: {
    email: string
  }
}

const SelectPage: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { i18n } = useTranslation()
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([])
  // Auto-advance timeout is driven by the saved project config; 0 = no timeout.
  const [remainingTime, setRemainingTime] = useState<number>(
    () => useConfigStore.getState().getPageSettings('select')?.selectionTimeout || 0
  )
  const [isProcessing, setIsProcessing] = useState(false)
  const [isPreviewReady, setIsPreviewReady] = useState(false)
  const previewRef = useRef<PrintPreviewRef>(null)
  const uploadStartedRef = useRef<boolean>(false)
  const printJobSentRef = useRef<boolean>(false)
  const isMountedRef = useRef(true)
  const isProcessingRef = useRef(false)
  const handleContinueWithPhotosRef = useRef<((photos: string[]) => Promise<void>) | null>(null)
  const context = useOutletContext<UserContext | null>()

  const [mirroredPhotos, setMirroredPhotos] = useState<Set<string>>(new Set())
  const [isAllMirrored, setIsAllMirrored] = useState(false)

  const previewPhotoUrls = useMemo(
    () =>
      selectedPhotos.map((photo) =>
        mirroredPhotos.has(photo) ? `mirror:${photo}` : photo
      ),
    [selectedPhotos, mirroredPhotos]
  )

  const {
    id,
    setSessionState,
    gallery,
    galleryKey,
    userPreferences,
    logActivity
  } = useSessionStateStore()
  const [printGalleryQrOnStrip, setPrintGalleryQrOnStrip] = useState(
    () => userPreferences.printGalleryQrOnStrip !== false
  )
  const { config: globalConfig, getFrame, frames, getPageSettings } = useConfigStore()
  const selectionTimeout = getPageSettings('select')?.selectionTimeout || 0
  const { downloadLocal } = useSettingsStore()
  const { isPrintingEnabled, getPrinterForFrame } = usePrintStore()
  const [isOnline, setIsOnline] = useState(true)

  // Initialize countdown hook
  const selectCountdown = useCountdown(selectionTimeout)

  const validPhotos = gallery.capturedItems.filter((item) => item.photoUrl)
  const selectedFrame = getFrame(userPreferences.frameId || frames[0]._id)
  const maxSelectablePhotos = selectedFrame?.drawParams.images
    ? new Set(
        selectedFrame.drawParams.images
          .filter((image) => image.type.startsWith('photo'))
          .map((image) => image.type)
      ).size
    : 3

  const frameConfig = useMemo(
    () =>
      selectedFrame
        ? {
            id: selectedFrame._id,
            baseImage: selectedFrame.thumbnail,
            drawParams: selectedFrame.drawParams
          }
        : {
            id: 'preview',
            baseImage: '',
            drawParams: {
              canvasWidth: 213,
              canvasHeight: 652,
              backgroundColor: '#FFFFFF',
              images: []
            }
          },
    [selectedFrame]
  )

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  useEffect(() => {
    isMountedRef.current = true
    return (): void => {
      isMountedRef.current = false
    }
  }, [])

  /** Fresh max photo count from frame config (avoids stale closure in countdown). */
  const resolveMaxSelectableFromStore = (): number => {
    const { getFrame, frames } = useConfigStore.getState()
    const { userPreferences: prefs } = useSessionStateStore.getState()
    const frame = getFrame(prefs.frameId || frames[0]._id)
    if (!frame?.drawParams.images) return 3
    return new Set(
      frame.drawParams.images
        .filter((image) => image.type.startsWith('photo'))
        .map((image) => image.type)
    ).size
  }

  useEffect((): (() => void) => {
    if (selectionTimeout > 0) {
      selectCountdown.setOnTick(() => {
        setRemainingTime(selectCountdown.getRemainingSeconds())
      })

      selectCountdown.setOnComplete(() => {
        if (isProcessingRef.current) return
        const { gallery: g } = useSessionStateStore.getState()
        const vp = g.capturedItems.filter((item) => item.photoUrl)
        const maxSel = resolveMaxSelectableFromStore()

        setSelectedPhotos((prev) => {
          let finalSelection = prev
          if (prev.length < maxSel) {
            const remaining = maxSel - prev.length
            const unselected = vp
              .filter((item) => !prev.includes(item.photoUrl))
              .slice(0, remaining)
              .map((item) => item.photoUrl)
            finalSelection = [...prev, ...unselected]
          }
          setTimeout(() => {
            if (isMountedRef.current) {
              void handleContinueWithPhotosRef.current?.(finalSelection)
            }
          }, 500)
          return finalSelection
        })
      })

      selectCountdown.start()
    }
    stateUploader.uploadSessionState()

    const initializeMedia = async (): Promise<void> => {
      if (uploadStartedRef.current) return
      uploadStartedRef.current = true

      const session = useSessionStateStore.getState()
      const cfg = useConfigStore.getState()
      const items = session.gallery.capturedItems.filter((item) => item.photoUrl)
      const photoItems = items.map((item) => ({ photoUrl: item.photoUrl }))

      const ctx: ProcessingContext = {
        context,
        projectId,
        sessionId: session.id || '',
        galleryId: session.galleryKey || session.gallery.id,
        downloadLocal,
        isOnline
      }

      try {
        processAndUploadPhotos(photoItems, ctx)
          .then(() => {
            const g = useSessionStateStore.getState().gallery
            setSessionState({
              gallery: {
                ...g,
                individualPhotosUploaded: true,
                individualPhotosUploadedAt: Date.now()
              }
            })
          })
          .catch((err) => {
            console.warn('[Bahela Select] Individual photos upload failed:', err)
          })

        if (session.userPreferences.useGIF) {
          try {
            const photoUrls = items.map((item) => item.photoUrl)
            const gifSpeed = cfg.config?.pages.capture.settings.gifSpeed || 1000
            const customGifFrame = cfg.config?.pages.capture.settings.customGifFrame
            const { gifLocalUrl, mp4LocalUrl } = await processAndUploadGIF(
              photoUrls,
              gifSpeed,
              ctx,
              customGifFrame
            )
            const newItems: Array<{
              id: string
              animationUrl: string
              animationType: 'gif' | 'mp4'
              gifUrl: string
              photoUrl: string
              timestamp: Date
            }> = []
            if (mp4LocalUrl) {
              newItems.push({
                id: `dreambooth-animation-${session.gallery.id}`,
                animationUrl: mp4LocalUrl,
                animationType: 'mp4',
                gifUrl: '',
                photoUrl: '',
                timestamp: new Date()
              })
            }
            if (gifLocalUrl) {
              newItems.push({
                id: `dreambooth-animation-gif-${session.gallery.id}`,
                animationUrl: gifLocalUrl,
                animationType: 'gif',
                gifUrl: gifLocalUrl,
                photoUrl: '',
                timestamp: new Date()
              })
            }
            if (newItems.length > 0) {
              const latest = useSessionStateStore.getState().gallery.capturedItems
              setSessionState({
                gallery: {
                  capturedItems: [...latest, ...newItems]
                }
              })
            } else if (typeof navigator !== 'undefined' ? navigator.onLine : true) {
              cogoToast.error('Failed to upload animation')
            }
          } catch (error) {
            console.error('Error creating/uploading GIF/MP4:', error)
          }
        }
      } catch (error) {
        console.error('Error initializing media:', error)
      }
    }

    initializeMedia()

    return () => {
      selectCountdown.stop()
    }
  }, [])

  // Check internet connectivity for offline detection
  useEffect(() => {
    if (typeof window.api !== 'undefined' && window.api.getInternetStatus) {
      window.api.getInternetStatus().then(setIsOnline)

      const unsubscribe = window.api.onInternetStatusChanged((status: boolean) => {
        setIsOnline(status)
      })

      return unsubscribe
    }
    return (): void => {}
  }, [])

  useEffect(() => {
    let cancelled = false
    setIsPreviewReady(false)
    const run = async (): Promise<void> => {
      if (!previewRef.current) return
      try {
        await previewRef.current.waitForReady(10000)
        if (!cancelled) setIsPreviewReady(true)
      } catch (error) {
        console.error('[Bahela Select] PrintPreview waitForReady:', error)
        setTimeout(() => {
          if (!cancelled) setIsPreviewReady(true)
        }, 5000)
      }
    }
    void run()
    return (): void => {
      cancelled = true
    }
  }, [previewPhotoUrls, frameConfig])

  const handlePhotoClick = (photoUrl: string): void => {
    if (isProcessing) return

    setSelectedPhotos((prev) => {
      if (prev.includes(photoUrl)) {
        return prev.filter((url) => url !== photoUrl)
      } else if (prev.length < maxSelectablePhotos) {
        return [...prev, photoUrl]
      }
      return prev
    })
  }

  const handleContinueWithPhotos = async (photosToProcess: string[]): Promise<void> => {
    if (isProcessingRef.current) return
    if (!isMountedRef.current) return

    if (!previewRef.current) {
      cogoToast.error('Preview component is not available. Please refresh the page.')
      console.error('previewRef.current is null when trying to continue')
      return
    }

    isProcessingRef.current = true
    setIsProcessing(true)
    const processingStartedAt = Date.now()
    let didAttemptPrint = false

    let uploadIsDone: boolean = false

    try {
      try {
        await previewRef.current.waitForReady(15000)
      } catch (e) {
        console.warn('[Bahela Select] waitForReady before export:', e)
      }

      let exportResult: ExportResult
      try {
        exportResult = await previewRef.current.exportImageWithStatus({
          timeoutMs: 8000,
          strict: true
        })
      } catch (err1) {
        console.warn('[Bahela Select] Strict export failed, retrying:', err1)
        try {
          exportResult = await previewRef.current.exportImageWithStatus({
            timeoutMs: 15000,
            strict: true
          })
        } catch (err2) {
          console.warn('[Bahela Select] Retry export failed, degraded fallback:', err2)
          // timeoutMs here only controls waitForReady (3 s is fine — assets are either loaded
          // by now or they're not coming). blobTimeoutMs is pixel-based and independent.
          exportResult = await previewRef.current.exportImageWithStatus({
            timeoutMs: 3000,
            strict: false
          })
        }
      }

      const stripBlob = exportResult.blob
      if (!stripBlob) throw new Error('Failed to export strip image')

      logActivity('Strip export completed', {
        blobSize: stripBlob.size,
        frameId: userPreferences.frameId,
        photoCount: photosToProcess.length,
        assetsVerified: exportResult.assetsVerified,
        degradedReason: exportResult.degradedReason ?? null
      })

      // Publish the strip to session state NOW, before print/upload. processStrip()
      // below races a 20s timeout; on a slow/offline booth the navigation to /result
      // wins that race and the Result page mounts with no strip item — an empty
      // result screen even though the strip exported fine. The blob URL is replaced
      // with the persisted local URL once processAndUploadStrip returns.
      const stripItemId = `dreambooth-strip-${gallery.id}`
      const immediateStripUrl = URL.createObjectURL(stripBlob)
      setSessionState({
        gallery: {
          // Mirror the strip onto the TOP-LEVEL gallery.stripUrl too. `gallery` is shallow-merged
          // by setSessionState, so this field survives even if a concurrent capturedItems update
          // (photo/video upload writeback with a stale snapshot) clobbers the strip ARRAY item —
          // the reprint modal reads gallery.stripUrl as a fallback, so it can no longer get stuck
          // on "Preparing your strip…" once the strip exists.
          stripUrl: immediateStripUrl,
          capturedItems: [
            ...useSessionStateStore.getState().gallery.capturedItems.filter(
              (item) => item.id !== stripItemId
            ),
            {
              id: stripItemId,
              stripUrl: immediateStripUrl,
              photoUrl: '',
              timestamp: new Date()
            }
          ]
        }
      })

      const processStrip = async (): Promise<void> => {
        if (!previewRef.current) {
          throw new Error('Print component is not available')
        }

        const ctx: ProcessingContext = {
          context,
          projectId,
          sessionId: id || '',
          galleryId: galleryKey || gallery.id,
          downloadLocal,
          isOnline: true
        }

        if (isPrintingEnabled && !printJobSentRef.current) {
          didAttemptPrint = true
          printJobSentRef.current = true
          const { printerName, settings } = getPrinterForFrame(userPreferences.frameId || '')
          try {
            await initiatePrint(
              stripBlob,
              printerName,
              userPreferences.copies,
              settings,
              true,
              undefined,
              'session',
              // Without these the print-event row carries only the deviceId, so a
              // paid session cannot be matched to its print and a failed print is
              // invisible to the server-side printer alerts.
              { sessionId: id || undefined, projectId: projectId || undefined }
            )
          } catch {
            // Logged in printService / store
          }
        }

        const stripResult = await processAndUploadStrip(stripBlob, ctx)
        // `|| immediateStripUrl` (not `??`): a falsy/empty localUrl must still fall back to the
        // eager blob so the strip preview never goes blank.
        const localStripUrl = stripResult?.localUrl || immediateStripUrl

        // Swap the eagerly-published blob URL (added right after export) for the
        // persisted local URL. Filter+append (not map) so the item also recovers if
        // something cleared capturedItems in between. Also stamp the uploaded (R2) URL as a
        // fallback source the reprint modal can print from when the local blob is unavailable.
        const cap = useSessionStateStore
          .getState()
          .gallery.capturedItems.filter((item) => item.id !== stripItemId)
        setSessionState({
          gallery: {
            // Persist the durable strip URL top-level (prefer the uploaded R2 URL — it outlives
            // the local blob on web). Survives capturedItems churn; see the eager-publish note.
            stripUrl: stripResult?.uploadedUrl || localStripUrl,
            capturedItems: [
              ...cap,
              {
                id: stripItemId,
                stripUrl: localStripUrl,
                stripRemoteUrl: stripResult?.uploadedUrl,
                photoUrl: '',
                timestamp: new Date()
              }
            ]
          }
        })

        // Generate and upload framed video (strip_live / strip_story) when recording enabled
        if (
          userPreferences.useRecording &&
          ['recording', 'strip_live', 'strip_story'].includes(
            userPreferences.recordingType || ''
          )
        ) {
          const resources = photosToProcess.map((photoUrl) => {
            const item = gallery.capturedItems.find(
              (i) => i.photoUrl === photoUrl || i.originalPhotoUrl === photoUrl
            )
            if (!item) throw new Error(`Could not find captured item for photo ${photoUrl}`)
            const selectedFilterId = userPreferences.filterId
            const photoToUse =
              selectedFilterId && item.filteredVersions?.[selectedFilterId]
                ? item.filteredVersions[selectedFilterId]
                : item.photoUrl
            return {
              photoUrl: photoToUse,
              videoUrl: item.recordingUrl || ''
            }
          })

          const photoStates = previewRef.current?.getPhotoStates() || {}
          const unscaledFrame = getFrame(
            userPreferences.frameId || frames[0]._id,
            1
          )
          const unscaledFrameConfig: FrameConfig = unscaledFrame
            ? {
                id: unscaledFrame._id,
                baseImage: unscaledFrame.thumbnail,
                drawParams: unscaledFrame.drawParams
              }
            : {
                id: '',
                baseImage: '',
                drawParams: {
                  canvasWidth: 1200,
                  canvasHeight: 1800,
                  backgroundColor: '#FFFFFF',
                  images: []
                }
              }

          setSessionState({
            gallery: {
              ...gallery,
              liveModeProcessing: true,
              liveModeUrl: null,
              liveModeLocalUrl: undefined,
              liveModeRemoteUrl: undefined
            }
          })

          const taskId =
            userPreferences.recordingType === 'strip_story'
              ? `video-story-${id}-${Date.now()}`
              : `video-live-${id}-${Date.now()}`
          const captureCountdownSeconds =
            globalConfig?.pages.capture?.settings?.captureCountdown || 3
          const captureCountdownMs = captureCountdownSeconds * 1000

          backgroundVideoProcessor.processVideo({
            id: taskId,
            type:
              userPreferences.recordingType === 'strip_story' ? 'story' : 'live',
            resources,
            frameConfig: unscaledFrameConfig,
            sessionId: id || '',
            userId: context?.user?.email ?? '',
            projectId: projectId ?? '',
            galleryId: galleryKey || gallery.id,
            speed: globalConfig?.pages.capture?.settings?.recordingSpeed || 1,
            fps: 30,
            photoStates,
            countdown: captureCountdownMs,
            onComplete: (uploadedUrl: string, localUrl?: string) => {
              const { playbackUrl, videoRemoteUrl } = resolveFramedVideoPlaybackAndRemote(
                uploadedUrl,
                localUrl
              )
              const current = useSessionStateStore.getState().gallery
              setSessionState({
                gallery: {
                  ...current,
                  liveModeProcessing: false,
                  liveModeUrl: playbackUrl || null,
                  liveModeLocalUrl: localUrl,
                  liveModeRemoteUrl: videoRemoteUrl
                }
              })
            },
            onError: (error) => {
              console.error('[Select] Video processing error:', error)
              logActivity(`Video processing failed: ${error.message}`, {
                taskId,
                level: 'error'
              })
              const current = useSessionStateStore.getState().gallery
              setSessionState({
                gallery: {
                  ...current,
                  liveModeProcessing: false,
                  liveModeUrl: null,
                  liveModeLocalUrl: undefined,
                  liveModeRemoteUrl: undefined
                }
              })
              cogoToast.error(
                i18n.language === 'id'
                  ? 'Gagal memproses video'
                  : 'Error processing video'
              )
            }
          })
        }

        // Note: Final session upload with isCompleted will be handled by Result page
        uploadIsDone = true
      }

      // Timeout fallback in 20s
      const timeout = new Promise<void>((resolve) =>
        setTimeout(() => {
          if (!uploadIsDone) {
            cogoToast.warn(
              'Proses mengunggah berjalan terlalu lama. Silakan cek galeri Anda secara berkala...'
            )
          }
          resolve()
        }, 20000)
      )

      await Promise.race([processStrip(), timeout])
      const minDisplaySec = useSettingsStore.getState().printResultMinDisplaySeconds
      if (didAttemptPrint && minDisplaySec > 0) {
        await waitPrintResultMinDisplay(processingStartedAt, minDisplaySec, {
          isStillMounted: () => isMountedRef.current
        })
      }
      navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
    } catch (error) {
      console.error('Error in processing:', error)
      cogoToast.error(
        'Terjadi kesalahan dalam pemrosesan hasil sesi foto. Silakan laporkan masalah ini.'
      )
      navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
    } finally {
      isProcessingRef.current = false
      setIsProcessing(false)
    }
  }

  useEffect(() => {
    handleContinueWithPhotosRef.current = handleContinueWithPhotos
  })

  const handleContinue = async (): Promise<void> => {
    if (isProcessingRef.current) return

    if (!previewRef.current) {
      cogoToast.error('Preview component is not available. Please refresh the page.')
      return
    }

    if (!isPreviewReady) {
      cogoToast.warn(
        i18n.language === 'id'
          ? 'Pratinjau masih dimuat. Mohon tunggu...'
          : 'Preview is still loading. Please wait...'
      )
      return
    }

    let photosToProcess = [...selectedPhotos]
    if (photosToProcess.length < maxSelectablePhotos) {
      const add = validPhotos
        .filter((item) => !photosToProcess.includes(item.photoUrl))
        .slice(0, maxSelectablePhotos - photosToProcess.length)
        .map((item) => item.photoUrl)
      photosToProcess = [...photosToProcess, ...add]
      setSelectedPhotos(photosToProcess)
      await new Promise((resolve) => setTimeout(resolve, 500))
    }

    await handleContinueWithPhotos(photosToProcess)
  }

  // Call all hooks at the top level
  const cachedLoadingImage = useCachedBackgroundImage(loadingImage)
  const cachedBgLayer = useCachedBackgroundImage(bgLayer)
  const cachedBahelaLogo = useCachedImage(bahelaLogo)

  return (
    <div className="relative bg-white w-full h-full overflow-hidden">

      {/* Loading Overlay */}
      {isProcessing && (
        <div
          className="absolute inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center"
          style={{
            backgroundImage: `url(${cachedLoadingImage})`,
            backgroundSize: 'cover'
          }}
        ></div>
      )}

      {/* Main Content */}
      <div className="absolute top-[86px] left-[220px] flex flex-col gap-[51px]">
        <div className="text-left relative w-auto">
          <h2 className="font-serif-bahela text-uriYellow text-[60px]">
            {i18n.language === 'id' ? 'Mau cetak yang mana?' : 'Pick your photos'}
            <span className="absolute -left-[2px] -top-[2px] text-hibiscus">
              {i18n.language === 'id' ? 'Mau cetak yang mana?' : 'Pick your photos'}
            </span>
          </h2>
        </div>

        {/* Photo Grid */}
        <div className="w-full h-[601px] grid grid-cols-3 gap-[69px] content-start overflow-y-auto overscroll-contain pr-4">
          {validPhotos.map((item, index) => {
            const isSelected = selectedPhotos.includes(item.photoUrl)
            const isMirrored = mirroredPhotos.has(item.photoUrl)
            return (
              <CachedPhotoImage
                key={index}
                photoUrl={item.photoUrl}
                isSelected={isSelected}
                isMirrored={isMirrored}
                index={index}
                onPhotoClick={handlePhotoClick}
                selectedIndex={isSelected ? selectedPhotos.indexOf(item.photoUrl) + 1 : undefined}
              />
            )
          })}
        </div>
      </div>

      {/* Right Panel */}
      <div
        className="bg-hibiscus absolute top-0 right-0 py-[100px] px-[100px] h-full flex flex-col items-center justify-center gap-[30px]"
        style={{ backgroundImage: `url(${cachedBgLayer})` }}
      >
        {/* Cermin + QR Galeri — one centered row above the strip preview */}
        <div className="absolute top-[40px] left-0 right-0 z-10 flex items-center justify-center gap-4 px-4">
          <button
            type="button"
            onClick={() => {
              if (isProcessing) return
              const newMirroredPhotos = new Set(mirroredPhotos)
              const willMirrorAll = validPhotos.some((item) => !mirroredPhotos.has(item.photoUrl))

              validPhotos.forEach((item) => {
                if (willMirrorAll) {
                  newMirroredPhotos.add(item.photoUrl)
                } else {
                  newMirroredPhotos.delete(item.photoUrl)
                }
              })

              setIsAllMirrored(willMirrorAll)
              setMirroredPhotos(newMirroredPhotos)
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 shadow-lg ${
              isAllMirrored
                ? 'bg-uriYellow text-methadone hover:bg-opacity-90'
                : 'bg-white text-methadone hover:bg-gray-100'
            } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isProcessing}
          >
            <FaExchangeAlt className="text-lg text-methadone" />
            <span className="font-body-bahela text-[20px] font-bold">
              {i18n.language === 'id' ? 'Cermin' : 'Mirror'}
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              if (isProcessing) return
              const next = !printGalleryQrOnStrip
              setPrintGalleryQrOnStrip(next)
              setSessionState({ userPreferences: { printGalleryQrOnStrip: next } })
              logActivity(`Gallery QR on strip ${next ? 'enabled' : 'disabled'}`)
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 shadow-lg ${
              printGalleryQrOnStrip
                ? 'bg-uriYellow text-methadone hover:bg-opacity-90'
                : 'bg-white text-methadone hover:bg-gray-100'
            } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isProcessing}
          >
            <FaQrcode className="text-lg text-methadone" />
            <span className="font-body-bahela text-[20px] font-bold">
              {i18n.language === 'id' ? 'QR Galeri' : 'Gallery QR'}
            </span>
          </button>
        </div>

        <div className="relative">
          <PrintPreview
            ref={previewRef}
            frameConfig={frameConfig}
            selectedPhotos={selectedPhotos.map((photo) =>
              mirroredPhotos.has(photo) ? `mirror:${photo}` : photo
            )}
            className="w-[400px] object-contain drop-shadow-2xl shadow-2xl"
            sessionId={id || galleryKey || gallery.id}
            includeQrOnStrip={printGalleryQrOnStrip}
          />
        </div>
        {selectionTimeout > 0 && (
          <div className="text-center text-white text-[50px] font-serif-bahela">
            {formatTime(remainingTime)}
          </div>
        )}
        <Button
          onClick={handleContinue}
          className={`text-[50px] ${
            selectedPhotos.length < maxSelectablePhotos || isProcessing || !isPreviewReady
              ? 'opacity-30'
              : 'opacity-100'
          }`}
          textColor="methadone"
          disabled={
            selectedPhotos.length < maxSelectablePhotos || isProcessing || !isPreviewReady
          }
        >
          {i18n.language === 'id' ? 'Lanjut' : 'Next'}
        </Button>
      </div>

      {/* Bahela Logo */}
      <img
        src={cachedBahelaLogo}
        alt="Bahela logo"
        className="absolute z-[1] bottom-[66px] left-[57px] w-[127px] h-[114px]"
      />
    </div>
  )
}

// Component for cached photo images
const CachedPhotoImage: React.FC<{
  photoUrl: string
  isSelected: boolean
  isMirrored: boolean
  index: number
  onPhotoClick: (photoUrl: string) => void
  selectedIndex?: number
}> = ({ photoUrl, isSelected, isMirrored, index, onPhotoClick, selectedIndex }) => {
  const cachedSrc = useCachedImage(photoUrl)

  return (
    <div className="relative w-[269px] h-[269px]">
      <div
        className={`absolute inset-0 overflow-hidden ${
          isSelected ? 'border-[8px] rounded-xl border-uriYellow' : ''
        }`}
      >
        <img
          src={cachedSrc}
          className={`w-full h-full object-cover cursor-pointer btn-shadow ${
            isMirrored ? 'scale-x-[-1]' : ''
          }`}
          onClick={() => onPhotoClick(photoUrl)}
          alt={`Captured ${index + 1}`}
        />
      </div>
      {isSelected && selectedIndex && (
        <div className="absolute bottom-4 right-4 drop-shadow-2xl w-[34px] h-[34px] rounded-full border-2 bg-grey border-uriYellow flex items-center justify-center">
          <span className="text-uriYellow text-xl">{selectedIndex}</span>
        </div>
      )}
    </div>
  )
}

export default SelectPage
